import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Menu, X, ArrowRight, ArrowUpRight, Package, Warehouse, TrendingUp, Zap,
  Shield, Globe, AlertTriangle, Truck, Radar, MapPin, Phone, Mail, Clock,
  CheckCircle2, Search, Boxes, PackageCheck, Activity, Plane, Ship,
  Navigation, RotateCcw, Building2,
} from "lucide-react";
import { customers, stats, contactInfo } from "../mock";
import "./v2.css";

const API = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG =
  "https://images.unsplash.com/photo-1726776230760-ae81dc9d4e55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBhZXJpYWwlMjB0cnVja3N8ZW58MHx8fHwxNzg5MzYzOTMyfDA&ixlib=rb-4.1.0&q=85";
const IMG_RACKING =
  "https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsb2dpc3RpY3MlMjB3YXJlaG91c2UlMjBhZXJpYWwlMjB0cnVja3N8ZW58MHx8fHwxNzg5MzYzOTMyfDA&ixlib=rb-4.1.0&q=85";
const IMG_YARD =
  "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxjYXJnbyUyMGZyZWlnaHQlMjBkaXN0cmlidXRpb24lMjBzdXBwbHklMjBjaGFpbnxlbnwwfHx8fDE3ODkzNjM5MzJ8MA&ixlib=rb-4.1.0&q=85";
const IMG_WORKER =
  "https://images.unsplash.com/photo-1764795850513-fa8a469a4600?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHx3YXJlaG91c2UlMjB3b3JrZXIlMjBpbnZlbnRvcnklMjB0ZWNobm9sb2d5JTIwdGFibGV0fGVufDB8fHx8MTc4OTM2MzkzMnww&ixlib=rb-4.1.0&q=85";
const IMG_HARDHAT =
  "https://images.unsplash.com/photo-1709803857195-dbd9eb6dc4bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHx3YXJlaG91c2UlMjB3b3JrZXIlMjBpbnZlbnRvcnklMjB0ZWNobm9sb2d5JTIwdGFibGV0fGVufDB8fHx8MTc4OTM2MzkzMnww&ixlib=rb-4.1.0&q=85";

// Scroll reveal hook
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".sabey-v2 .v2-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const NAV = [
  { name: "Solutions", href: "#solutions" },
  { name: "Services", href: "#services" },
  { name: "Control Tower", href: "#technology" },
  { name: "Network", href: "#network" },
  { name: "Contact", href: "#contact" },
];

const SERVICES = [
  { icon: Warehouse, title: "Field Stocking Locations", desc: "Forward-positioned inventory across the U.S. for same-hour parts deployment and drastically reduced lead times." },
  { icon: TrendingUp, title: "Supply Chain Management", desc: "End-to-end orchestration that optimizes cost, velocity, and visibility across your entire network." },
  { icon: Package, title: "Warehousing & Fulfillment", desc: "Secure, SKU-serialized warehousing with real-time inventory and precision order fulfillment." },
  { icon: Zap, title: "Rush & Critical Delivery", desc: "2-hour, 4-hour, and same-day tiers engineered for mission-critical, time-sensitive freight." },
  { icon: AlertTriangle, title: "HAZMAT Certified Transport", desc: "Full DOT-compliant handling and transport of dangerous goods with specialized safety protocols." },
  { icon: Shield, title: "TSA-Certified Operations", desc: "Vetted, TSA-certified handling for regulated, sensitive, and high-value shipments." },
  { icon: Globe, title: "International Forwarding", desc: "Global air freight, customs, and cross-border forwarding connecting you to markets worldwide." },
];

const PILLARS = [
  { icon: Radar, kicker: "01 / Control Tower", title: "A 24/7/365 control tower", desc: "Our operations never sleep. A dedicated control tower monitors every shipment in real time and intervenes before a delay becomes a disruption.", img: IMG_HARDHAT },
  { icon: Boxes, kicker: "02 / Field Stocking", title: "Inventory positioned where it matters", desc: "Strategically located field-stocking sites keep your critical parts minutes from the point of need, nationwide.", img: IMG_WORKER },
];

const LIFECYCLE = [
  { icon: Navigation, title: "Network Engineering", desc: "We map demand and position FSL nodes for optimal coverage." },
  { icon: PackageCheck, title: "Inbound & Serialization", desc: "Receipt, SKU-level serialization, and secure storage." },
  { icon: Truck, title: "Dispatch & Rush Delivery", desc: "24/7 real-time dispatch with critical same-day tiers." },
  { icon: RotateCcw, title: "Reverse Logistics", desc: "Asset recovery, repair routing, and closed-loop returns." },
];

const HUBS = [
  { city: "Seattle, WA", sla: "2-Hour SLA", skus: "18,400 SKUs", note: "Pacific Northwest control tower & HQ" },
  { city: "Los Angeles, CA", sla: "2-Hour SLA", skus: "22,100 SKUs", note: "West coast air-freight gateway" },
  { city: "Dallas, TX", sla: "4-Hour SLA", skus: "15,700 SKUs", note: "Central distribution & cross-dock" },
  { city: "Chicago, IL", sla: "4-Hour SLA", skus: "19,800 SKUs", note: "Midwest rail & road interchange" },
  { city: "Atlanta, GA", sla: "4-Hour SLA", skus: "14,200 SKUs", note: "Southeast fulfillment hub" },
  { city: "New York, NY", sla: "2-Hour SLA", skus: "20,600 SKUs", note: "Northeast metro rush corridor" },
];

const TECH_FEATURES = [
  { icon: Activity, label: "SKU-level live tracking" },
  { icon: Radar, label: "Telemetry & condition logs" },
  { icon: Boxes, label: "Automated reorder alerts" },
  { icon: Globe, label: "API & EDI integration" },
];

const Section = ({ id, className = "", style, children }) => (
  <section id={id} className={className} style={style}>
    {children}
  </section>
);

const HomeV2 = () => {
  useReveal();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [track, setTrack] = useState("");
  const [trackResult, setTrackResult] = useState(null);
  const [activeHub, setActiveHub] = useState(0);
  const contactRef = useRef(null);

  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", service: "",
    origin: "", destination: "", urgency: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitState, setSubmitState] = useState(null); // 'ok' | 'err'

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const runTrack = (e) => {
    e.preventDefault();
    if (!track.trim()) return;
    setTrackResult({
      id: track.trim().toUpperCase(),
      status: "In Transit",
      location: "Chicago, IL Distribution Center",
      next: "Out for delivery within 3 hrs",
    });
  };

  const onField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitQuote = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitState(null);
    const message = [
      form.message,
      "",
      `Service: ${form.service || "—"}`,
      `Origin ZIP: ${form.origin || "—"}`,
      `Destination ZIP: ${form.destination || "—"}`,
      `Urgency: ${form.urgency || "—"}`,
    ].join("\n");
    try {
      const res = await fetch(`${API}/api/contact/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          company: form.company || null,
          service: form.service || null,
          message: message || "Quote request",
        }),
      });
      if (!res.ok) throw new Error("bad");
      setSubmitState("ok");
      setForm({ name: "", email: "", phone: "", company: "", service: "", origin: "", destination: "", urgency: "", message: "" });
    } catch (_) {
      setSubmitState("err");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToContact = () => contactRef.current?.scrollIntoView({ behavior: "smooth" });

  const marqueeHalf = customers;

  return (
    <div className="sabey-v2 min-h-screen">
      {/* Compare ribbon */}
      <div className="w-full text-white text-center text-xs sm:text-sm py-2 px-4" style={{ background: "var(--v2-slate)" }}>
        <span className="v2-mono tracking-wider opacity-90">PREVIEW · NEW DESIGN (v2)</span>
        <span className="mx-2 opacity-40">|</span>
        <Link to="/" data-testid="v2-back-to-current" className="underline underline-offset-4 hover:text-amber-400 transition-colors">
          ← Back to current live site
        </Link>
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-50 v2-glass transition-shadow ${scrolled ? "shadow-[0_8px_30px_-12px_rgba(15,23,42,0.15)]" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="#top" className="flex items-center gap-3" data-testid="v2-logo">
              <div className="relative p-2.5 rounded-xl" style={{ background: "var(--v2-navy)" }}>
                <Package className="h-6 w-6 text-white" />
                <span className="absolute -top-0.5 -right-0.5 v2-dot" />
              </div>
              <div className="leading-none">
                <div className="v2-display text-xl font-extrabold" style={{ color: "var(--v2-navy)" }}>SABEY</div>
                <div className="v2-mono text-[10px] tracking-[0.3em] text-slate-500 mt-0.5">LOGISTICS</div>
              </div>
            </a>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((n) => (
                <a key={n.href} href={n.href} className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors">
                  {n.name}
                </a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Link to="/tracking" data-testid="v2-nav-track" className="px-4 py-2 rounded-full text-sm font-semibold v2-btn-dark">
                Track Order
              </Link>
              <button onClick={scrollToContact} data-testid="v2-nav-quote" className="v2-btn-gold px-5 py-2.5 text-sm inline-flex items-center gap-2">
                Request Quote <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setMenuOpen(!menuOpen)} data-testid="v2-mobile-toggle">
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-1">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="block px-4 py-3 rounded-lg font-semibold text-slate-700 hover:bg-slate-50">
                {n.name}
              </a>
            ))}
            <div className="pt-2 flex gap-3">
              <Link to="/tracking" className="flex-1 text-center px-4 py-3 rounded-full font-semibold v2-btn-dark">Track Order</Link>
              <button onClick={() => { setMenuOpen(false); scrollToContact(); }} className="flex-1 v2-btn-gold px-4 py-3">Request Quote</button>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <Section id="top" className="relative overflow-hidden" style={{ background: "var(--v2-navy)" }}>
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt="Sabey Logistics distribution hub" className="w-full h-full object-cover" />
          <div className="absolute inset-0 v2-hero-scrim" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-36">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 text-white">
              <div className="v2-reveal inline-flex items-center gap-2 v2-glass-dark rounded-full px-4 py-2 mb-8">
                <span className="v2-dot" />
                <span className="v2-overline text-amber-400">Mission-critical supply chain logistics</span>
              </div>
              <h1 className="v2-reveal v2-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">
                Precision logistics<br />
                <span style={{ color: "var(--v2-gold)" }}>when every minute</span><br />
                costs millions
              </h1>
              <p className="v2-reveal mt-6 text-lg sm:text-xl text-slate-200/90 max-w-xl leading-relaxed">
                24/7/365 field stocking, rush &amp; HAZMAT delivery, and TSA-certified forwarding — tracked in real time through the Sabey Control Tower.
              </p>

              {/* Quick track */}
              <form onSubmit={runTrack} className="v2-reveal mt-8 max-w-md">
                <div className="flex items-stretch v2-glass-dark rounded-full p-1.5">
                  <div className="flex items-center pl-4 pr-2 text-slate-300"><Search className="h-5 w-5" /></div>
                  <input
                    value={track}
                    onChange={(e) => setTrack(e.target.value)}
                    data-testid="v2-track-input"
                    placeholder="Enter tracking number"
                    className="flex-1 bg-transparent text-white placeholder:text-slate-400 outline-none text-sm py-3"
                  />
                  <button type="submit" data-testid="v2-track-submit" className="v2-btn-gold px-5 text-sm rounded-full">Track</button>
                </div>
                {trackResult && (
                  <div data-testid="v2-track-result" className="mt-3 v2-glass-dark rounded-2xl p-4 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="v2-mono text-amber-400">{trackResult.id}</span>
                      <span className="inline-flex items-center gap-1.5 text-emerald-400 font-semibold"><span className="v2-dot" />{trackResult.status}</span>
                    </div>
                    <div className="mt-2 text-slate-200">{trackResult.location}</div>
                    <div className="text-slate-400">{trackResult.next}</div>
                  </div>
                )}
              </form>

              <div className="v2-reveal mt-8 flex flex-wrap gap-4">
                <button onClick={scrollToContact} data-testid="v2-hero-quote" className="v2-btn-gold px-7 py-3.5 inline-flex items-center gap-2">
                  Request Custom Quote <ArrowRight className="h-5 w-5" />
                </button>
                <a href="#technology" className="v2-btn-ghost px-7 py-3.5 inline-flex items-center gap-2">
                  Explore Control Tower <ArrowUpRight className="h-5 w-5" />
                </a>
              </div>
            </div>

            {/* Floating telemetry card */}
            <div className="lg:col-span-5 hidden lg:block">
              <div className="v2-reveal v2-glass-dark rounded-3xl p-6 text-white">
                <div className="flex items-center justify-between mb-5">
                  <span className="v2-overline text-amber-400">Live Control Tower</span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400"><span className="v2-dot" /> Online</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { icon: Truck, k: "In transit", v: "1,284" },
                    { icon: PackageCheck, k: "Delivered today", v: "3,912" },
                    { icon: MapPin, k: "Active FSLs", v: "Nationwide" },
                    { icon: Clock, k: "On-time SLA", v: "99.8%" },
                  ].map((s, i) => (
                    <div key={i} className="rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.06)" }}>
                      <s.icon className="h-5 w-5 text-amber-400 mb-3" />
                      <div className="v2-display text-2xl font-bold">{s.v}</div>
                      <div className="text-xs text-slate-300 mt-0.5">{s.k}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-2xl p-4 flex items-center gap-3" style={{ background: "rgba(217,119,6,0.14)" }}>
                  <Radar className="h-5 w-5 text-amber-400" />
                  <span className="text-sm text-slate-100">Real-time telemetry on every critical shipment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Trust marquee */}
      <Section className="py-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 text-center">
          <p className="v2-overline">Trusted by 140+ enterprise & government leaders</p>
        </div>
        <div className="v2-marquee relative overflow-hidden">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-r from-white to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 z-10 bg-gradient-to-l from-white to-transparent" />
          <div className="v2-track v2-scroll-l">
            {[...marqueeHalf, ...marqueeHalf].map((c, i) => (
              <div key={`m1-${c.name}-${i}`} className="flex-shrink-0 w-36 h-16 mr-6 flex items-center justify-center px-3 rounded-xl border border-slate-100 bg-slate-50/60">
                <img
                  src={c.logo}
                  alt={c.name}
                  className="max-w-[104px] max-h-[40px] object-contain opacity-60 grayscale hover:grayscale-0 hover:opacity-100 transition"
                  onError={(e) => { e.target.style.display = "none"; e.target.parentElement.innerHTML = `<span class='text-xs font-semibold text-slate-500'>${c.name}</span>`; }}
                />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Capabilities pillars */}
      <Section id="solutions" className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16 v2-reveal">
            <p className="v2-overline mb-3">The Sabey difference</p>
            <h2 className="v2-display text-3xl lg:text-4xl font-bold" style={{ color: "var(--v2-navy)" }}>
              Built for the shipments that cannot fail
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              When downtime is measured in dollars per second, generic carriers aren't enough. Sabey pairs forward-positioned inventory with a live control tower so the right part arrives at the right place — every time.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {PILLARS.map((p, i) => (
              <div key={i} className="v2-reveal v2-lift rounded-3xl overflow-hidden border border-slate-200 bg-white" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="relative h-56">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,43,0) 40%, rgba(11,19,43,0.75) 100%)" }} />
                  <span className="absolute top-4 left-4 v2-mono text-xs tracking-widest text-amber-300">{p.kicker}</span>
                </div>
                <div className="p-8">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: "rgba(217,119,6,0.12)" }}>
                    <p.icon className="h-6 w-6" style={{ color: "var(--v2-gold)" }} />
                  </div>
                  <h3 className="v2-display text-2xl font-semibold mb-3" style={{ color: "var(--v2-navy)" }}>{p.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Lifecycle */}
          <div className="mt-20">
            <div className="v2-reveal text-center max-w-2xl mx-auto mb-14">
              <p className="v2-overline mb-3">End-to-end lifecycle</p>
              <h3 className="v2-display text-2xl lg:text-3xl font-bold" style={{ color: "var(--v2-navy)" }}>
                One partner, the full circular supply chain
              </h3>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {LIFECYCLE.map((s, i) => (
                <div key={i} className="v2-reveal relative rounded-3xl p-7 border border-slate-200 bg-slate-50/60 v2-lift" style={{ transitionDelay: `${i * 70}ms` }}>
                  <div className="v2-mono text-xs text-slate-400 mb-4">0{i + 1}</div>
                  <s.icon className="h-7 w-7 mb-4" style={{ color: "var(--v2-gold)" }} />
                  <h4 className="font-semibold text-lg mb-2" style={{ color: "var(--v2-navy)" }}>{s.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Stats band */}
      <Section className="py-20" style={{ background: "var(--v2-navy)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="v2-reveal text-center lg:text-left" style={{ transitionDelay: `${i * 70}ms` }}>
                <div className="v2-display text-4xl lg:text-5xl font-extrabold text-white">{s.value}</div>
                <div className="mt-2 v2-mono text-xs tracking-widest text-amber-400 uppercase">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Services */}
      <Section id="services" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
            <div className="max-w-2xl v2-reveal">
              <p className="v2-overline mb-3">Integrated services</p>
              <h2 className="v2-display text-3xl lg:text-4xl font-bold" style={{ color: "var(--v2-navy)" }}>
                A complete logistics suite, one point of contact
              </h2>
            </div>
            <button onClick={scrollToContact} className="v2-arrow-link v2-reveal" data-testid="v2-services-cta">
              Discuss your requirements <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <div key={i} className="v2-reveal group v2-lift rounded-3xl p-8 border border-slate-200 bg-white" style={{ transitionDelay: `${(i % 3) * 70}ms` }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors" style={{ background: "var(--v2-navy)" }}>
                  <s.icon className="h-6 w-6 text-amber-400" />
                </div>
                <h3 className="v2-display text-xl font-semibold mb-3" style={{ color: "var(--v2-navy)" }}>{s.title}</h3>
                <p className="text-slate-600 leading-relaxed text-[15px]">{s.desc}</p>
              </div>
            ))}
            {/* Accent CTA tile */}
            <div className="v2-reveal rounded-3xl p-8 flex flex-col justify-between text-white v2-lift" style={{ background: "var(--v2-gold)" }}>
              <Plane className="h-8 w-8" />
              <div className="mt-8">
                <h3 className="v2-display text-2xl font-bold mb-2">Need a tailored program?</h3>
                <p className="text-amber-50/90 mb-6 text-sm">Enterprise logistics engineered around your SLAs.</p>
                <button onClick={scrollToContact} className="inline-flex items-center gap-2 bg-white text-amber-700 font-semibold rounded-full px-5 py-2.5 hover:bg-amber-50 transition-colors" data-testid="v2-services-tile-cta">
                  Talk to us <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Technology / Control Tower */}
      <Section id="technology" className="py-20 lg:py-32" style={{ background: "var(--v2-slate)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div className="v2-reveal order-2 lg:order-1">
              <div className="relative rounded-3xl overflow-hidden border border-white/10">
                <img src={IMG_WORKER} alt="Sabey Control Tower live visibility" className="w-full h-[440px] object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(28,37,65,0) 55%, rgba(28,37,65,0.6) 100%)" }} />
                <div className="absolute bottom-5 left-5 right-5 v2-glass-dark rounded-2xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-white">
                    <span className="v2-dot" />
                    <span className="text-sm font-semibold">SBYL-284193 · In Transit</span>
                  </div>
                  <span className="v2-mono text-xs text-amber-400">ETA 2h 14m</span>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 text-white">
              <p className="v2-overline text-amber-400 mb-3 v2-reveal">Technology</p>
              <h2 className="v2-reveal v2-display text-3xl lg:text-4xl font-bold leading-tight">
                The Sabey Control Tower
              </h2>
              <p className="v2-reveal mt-4 text-lg text-slate-300 leading-relaxed">
                A single, cloud-based portal giving you real-time visibility into every order, SKU, and milestone — seamlessly integrated with our dispatch and field-stocking network.
              </p>
              <div className="v2-reveal mt-8 grid sm:grid-cols-2 gap-4">
                {TECH_FEATURES.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.05)" }}>
                    <f.icon className="h-5 w-5 text-amber-400 flex-shrink-0" />
                    <span className="text-sm text-slate-200">{f.label}</span>
                  </div>
                ))}
              </div>
              <div className="v2-reveal mt-8">
                <Link to="/tracking" data-testid="v2-tech-track-link" className="v2-btn-gold px-7 py-3.5 inline-flex items-center gap-2">
                  Track a shipment <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Coverage network */}
      <Section id="network" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 v2-reveal">
              <p className="v2-overline mb-3">Coverage</p>
              <h2 className="v2-display text-3xl lg:text-4xl font-bold" style={{ color: "var(--v2-navy)" }}>
                A nationwide field-stocking network
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Strategically positioned hubs put critical inventory minutes from your customers coast to coast — each backed by defined SLA windows and live stock levels.
              </p>
              <div className="mt-8 rounded-3xl overflow-hidden border border-slate-200">
                <img src={IMG_YARD} alt="Distribution network" className="w-full h-56 object-cover" />
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-4">
                {HUBS.map((h, i) => (
                  <button
                    key={i}
                    onMouseEnter={() => setActiveHub(i)}
                    onClick={() => setActiveHub(i)}
                    data-testid={`v2-hub-${i}`}
                    className={`text-left rounded-3xl p-6 border transition-colors ${activeHub === i ? "border-transparent text-white" : "border-slate-200 bg-white hover:border-slate-300"}`}
                    style={activeHub === i ? { background: "var(--v2-navy)" } : {}}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <MapPin className={`h-5 w-5 ${activeHub === i ? "text-amber-400" : ""}`} style={activeHub === i ? {} : { color: "var(--v2-gold)" }} />
                      <span className="font-semibold">{h.city}</span>
                    </div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`v2-mono text-xs px-2 py-1 rounded-full ${activeHub === i ? "bg-amber-500/20 text-amber-300" : "bg-slate-100 text-slate-600"}`}>{h.sla}</span>
                      <span className={`v2-mono text-xs ${activeHub === i ? "text-slate-300" : "text-slate-500"}`}>{h.skus}</span>
                    </div>
                    <p className={`text-sm ${activeHub === i ? "text-slate-300" : "text-slate-500"}`}>{h.note}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Contact / Quote */}
      <Section id="contact" className="py-20 lg:py-32" style={{ background: "var(--v2-navy)" }}>
        <div ref={contactRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Left info */}
            <div className="lg:col-span-5 text-white">
              <p className="v2-overline text-amber-400 mb-3 v2-reveal">Get started</p>
              <h2 className="v2-reveal v2-display text-3xl lg:text-4xl font-bold leading-tight">
                Request an enterprise quote
              </h2>
              <p className="v2-reveal mt-4 text-lg text-slate-300 leading-relaxed">
                Tell us about your shipment and SLA needs. Our control tower team will respond promptly with a tailored solution.
              </p>
              <div className="v2-reveal mt-10 space-y-5">
                {[
                  { icon: Phone, label: contactInfo.phone, sub: "24/7/365 control tower" },
                  { icon: Mail, label: contactInfo.email, sub: "Enterprise inquiries" },
                  { icon: Building2, label: contactInfo.address, sub: "Headquarters" },
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.06)" }}>
                      <c.icon className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <div className="font-semibold">{c.label}</div>
                      <div className="text-sm text-slate-400">{c.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7 v2-reveal">
              <form onSubmit={submitQuote} data-testid="v2-quote-form" className="rounded-3xl bg-white p-6 sm:p-8">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full name *"><input required value={form.name} onChange={onField("name")} data-testid="v2-form-name" className="v2-input" placeholder="Jane Doe" /></Field>
                  <Field label="Company email *"><input required type="email" value={form.email} onChange={onField("email")} data-testid="v2-form-email" className="v2-input" placeholder="jane@company.com" /></Field>
                  <Field label="Phone"><input value={form.phone} onChange={onField("phone")} data-testid="v2-form-phone" className="v2-input" placeholder="(555) 000-0000" /></Field>
                  <Field label="Company"><input value={form.company} onChange={onField("company")} data-testid="v2-form-company" className="v2-input" placeholder="Acme Corp" /></Field>
                  <Field label="Service type">
                    <select value={form.service} onChange={onField("service")} data-testid="v2-form-service" className="v2-input">
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
                    </select>
                  </Field>
                  <Field label="Urgency tier">
                    <select value={form.urgency} onChange={onField("urgency")} data-testid="v2-form-urgency" className="v2-input">
                      <option value="">Select urgency</option>
                      <option>2-Hour</option>
                      <option>4-Hour</option>
                      <option>Same-Day</option>
                      <option>Next-Day</option>
                    </select>
                  </Field>
                  <Field label="Origin ZIP"><input value={form.origin} onChange={onField("origin")} data-testid="v2-form-origin" className="v2-input" placeholder="98188" /></Field>
                  <Field label="Destination ZIP"><input value={form.destination} onChange={onField("destination")} data-testid="v2-form-destination" className="v2-input" placeholder="60601" /></Field>
                </div>
                <div className="mt-4">
                  <Field label="Shipment details *">
                    <textarea required rows={4} value={form.message} onChange={onField("message")} data-testid="v2-form-message" className="v2-input resize-none" placeholder="Package weight/dimensions, HAZMAT or TSA requirements, timeline..." />
                  </Field>
                </div>

                {submitState === "ok" && (
                  <div data-testid="v2-form-success" className="mt-4 flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium" style={{ background: "rgba(16,185,129,0.12)", color: "#047857" }}>
                    <CheckCircle2 className="h-5 w-5" /> Thank you — your request was sent to our control tower team.
                  </div>
                )}
                {submitState === "err" && (
                  <div data-testid="v2-form-error" className="mt-4 flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-medium" style={{ background: "rgba(239,68,68,0.1)", color: "#b91c1c" }}>
                    <AlertTriangle className="h-5 w-5" /> Something went wrong. Please try again or call us directly.
                  </div>
                )}

                <button type="submit" disabled={submitting} data-testid="v2-form-submit" className="v2-btn-gold w-full mt-6 py-4 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {submitting ? "Sending..." : <>Request Quote <ArrowRight className="h-5 w-5" /></>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid md:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2.5 rounded-xl" style={{ background: "var(--v2-navy)" }}><Package className="h-5 w-5 text-white" /></div>
                <div className="leading-none">
                  <div className="v2-display text-lg font-extrabold" style={{ color: "var(--v2-navy)" }}>SABEY</div>
                  <div className="v2-mono text-[10px] tracking-[0.3em] text-slate-500 mt-0.5">LOGISTICS</div>
                </div>
              </div>
              <p className="text-sm text-slate-500 leading-relaxed">Mission-critical supply chain logistics with a nationwide field-stocking network and 24/7 control tower.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4" style={{ color: "var(--v2-navy)" }}>Explore</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                {NAV.map((n) => <li key={n.href}><a href={n.href} className="hover:text-slate-900 transition-colors">{n.name}</a></li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4" style={{ color: "var(--v2-navy)" }}>Portals</h4>
              <ul className="space-y-2 text-sm text-slate-500">
                <li><Link to="/customer/login" className="hover:text-slate-900 transition-colors">Customer Login</Link></li>
                <li><Link to="/driver/login" className="hover:text-slate-900 transition-colors">Driver Login</Link></li>
                <li><Link to="/dispatcher/login" className="hover:text-slate-900 transition-colors">Dispatcher Login</Link></li>
                <li><Link to="/tracking" className="hover:text-slate-900 transition-colors">Track Shipment</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4" style={{ color: "var(--v2-navy)" }}>Contact</h4>
              <ul className="space-y-3 text-sm text-slate-500">
                <li className="flex items-start gap-2"><Phone className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "var(--v2-gold)" }} />{contactInfo.phone}</li>
                <li className="flex items-start gap-2"><Mail className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "var(--v2-gold)" }} />{contactInfo.email}</li>
                <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: "var(--v2-gold)" }} />{contactInfo.address}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
            <span>© {new Date().getFullYear()} Sabey Logistics. All rights reserved. · TSA Certified Operations</span>
            <Link to="/" className="v2-arrow-link" data-testid="v2-footer-back">← View current live site</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold text-slate-500 mb-1.5">{label}</span>
    {children}
  </label>
);

export default HomeV2;
